import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import {
  Building2, FileText, ReceiptIndianRupee, TrendingUp,
  MessageSquareWarning, Award, AlertTriangle, CheckCircle2, ArrowUpRight
} from "lucide-react";
import { Link } from "react-router-dom";

const StatTile = ({ icon: Icon, label, value, sub, tone = "default", testid }) => (
  <div className="stat-tile" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="w-9 h-9 rounded-md flex items-center justify-center"
           style={{ background: tone === "accent" ? "#FFF1E6" : "#EEF2F7", color: tone === "accent" ? "#E87722" : "#0A3161" }}>
        <Icon className="w-4 h-4" />
      </div>
      {sub && <span className="text-[10px] uppercase tracking-wider text-slate-400">{sub}</span>}
    </div>
    <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
    <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [panName, setPanName] = useState("");

  useEffect(() => {
    const pid = user?.panchayat_id;
    api.get("/dashboard/stats", { params: pid ? { panchayat_id: pid } : {} })
      .then((r) => setStats(r.data))
      .catch(() => {});
    if (pid) {
      api.get(`/panchayats/${pid}`).then((r) => setPanName(lang === "hi" ? (r.data.name_hi || r.data.name) : r.data.name)).catch(() => {});
    }
  }, [user, lang]);

  return (
    <OfficerLayout>
      <div className="max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("dashboard")}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">{t("welcome")}, {user?.full_name?.split(" ")[0]}</h1>
            <p className="mt-1 text-sm text-slate-600">
              {panName ? `${t("panchayat")}: ${panName}` : lang === "hi" ? "राज्य स्तरीय अवलोकन" : "Statewide overview"}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/tax" data-testid="quick-collect" className="px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#E87722" }}>
              {t("collectTax")}
            </Link>
            <Link to="/certificates" className="px-3 py-2 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161]">
              {t("issueCertificate")}
            </Link>
          </div>
        </div>

        {!stats ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile testid="stat-panchayats" icon={Building2} label={t("totalPanchayats")} value={stats.total_panchayats} />
              <StatTile testid="stat-properties" icon={FileText} label={t("totalProperties")} value={stats.total_properties} />
              <StatTile testid="stat-collected" icon={ReceiptIndianRupee} label={t("totalCollected")} value={formatINR(stats.total_collected)} sub={`of ${formatINR(stats.total_demand)}`} tone="accent" />
              <StatTile testid="stat-rate" icon={TrendingUp} label={t("collectionRate")} value={`${stats.collection_rate}%`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              {/* Tax breakdown */}
              <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("tax")}</div>
                    <h3 className="font-heading text-lg font-bold text-slate-900">Demand vs Collection</h3>
                  </div>
                  <Link to="/tax" className="text-xs font-semibold text-[#0A3161] inline-flex items-center gap-1">View all <ArrowUpRight className="w-3 h-3" /></Link>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>{t("totalCollected")}</span>
                      <span className="font-semibold text-slate-900">{formatINR(stats.total_collected)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${stats.collection_rate}%`, background: "#107C10" }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>{t("outstanding")}</span>
                      <span className="font-semibold text-slate-900">{formatINR(stats.outstanding)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${100 - stats.collection_rate}%`, background: "#E87722" }} />
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-2">
                  {[
                    { k: "pending", c: "#9CA3AF" },
                    { k: "partial", c: "#E87722" },
                    { k: "paid", c: "#107C10" },
                  ].map((s) => (
                    <div key={s.k} className="rounded-md border border-slate-200 p-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: s.c }} />
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t(s.k)}</span>
                      </div>
                      <div className="mt-1 font-heading text-xl font-bold text-slate-900">{stats.demands_by_status[s.k] || 0}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Grievances */}
              <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="stat-grievances">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("grievances")}</div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{stats.grievances.total} total</h3>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-3" data-testid="griev-open">
                    <div className="w-8 h-8 rounded-md bg-red-50 text-red-600 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">{t("openGrievances")}</div>
                      <div className="font-heading text-lg font-bold text-slate-900">{stats.grievances.open}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center">
                      <MessageSquareWarning className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">{t("inProgress")}</div>
                      <div className="font-heading text-lg font-bold text-slate-900">{stats.grievances.in_progress}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">{t("resolved")}</div>
                      <div className="font-heading text-lg font-bold text-slate-900">{stats.grievances.resolved}</div>
                    </div>
                  </div>
                </div>
                <Link to="/grievances" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#0A3161]">
                  Manage <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div className="rounded-md border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-slate-900">Recent Collections</h3>
                  <Link to="/tax" className="text-xs text-[#0A3161] font-semibold">View all</Link>
                </div>
                {stats.recent_receipts?.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No collections yet</div>
                ) : (
                  <div className="space-y-2">
                    {stats.recent_receipts?.map((r) => (
                      <div key={r.id} className="flex items-center justify-between text-sm border-b border-slate-100 last:border-0 py-2">
                        <div>
                          <div className="font-mono text-xs text-slate-500">{r.receipt_no}</div>
                          <div className="text-slate-700">{r.payer_name || "—"}</div>
                        </div>
                        <div className="font-heading font-bold text-slate-900">{formatINR(r.amount_paid)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-md border border-slate-200 bg-white p-5">
                <h3 className="font-heading font-semibold text-slate-900 mb-3">Quick Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { to: "/properties", l: t("newAssessment"), i: FileText },
                    { to: "/tax", l: t("collectTax"), i: ReceiptIndianRupee },
                    { to: "/certificates", l: t("issueCertificate"), i: Award },
                    { to: "/grievances", l: t("grievances"), i: MessageSquareWarning },
                  ].map((q) => {
                    const I = q.i;
                    return (
                      <Link key={q.to} to={q.to} className="flex items-center gap-2 p-3 rounded-md border border-slate-200 hover:border-[#0A3161] hover:bg-[#0A3161]/5 text-sm font-medium text-slate-700 transition-colors">
                        <I className="w-4 h-4 text-[#0A3161]" /> {q.l}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </OfficerLayout>
  );
}
